import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subscription, take } from 'rxjs';

import { AppService } from '../../../../app.service';
import { GlobalService } from '../../../../global/services/global.service';
import { generalConfigFromGlobal } from '../../../../shared/workspace-element-context';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import { ConfirmationModalConfig } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GridFactory } from '../../../../core/grid/services/grid.service';
import { environment } from '../../../../../environments/environment';

import { EFileService } from '../../../efile.service';
import { SubmissionService } from '../../../submission-data/submission.service';
import { Submission } from '../../../models/submission.model';
import {
  EfileGridCallbackEvent,
  EfileGridConfig,
  EfileGridDirective,
} from '../../../shared/efile-grid.directive';
import { EfileGridCacheService } from '../../../shared/efile-grid-cache.service';
import {
  CtrlFnPlan,
  EfileCtrlFnDispatcherService,
} from '../../scrubbing-actions/efile-ctrlfn-dispatcher.service';
import {
  EfileWorkflowService,
  ReturnReviewStep,
} from '../efile-workflow.service';

interface ErrorListRow {
  FORM: string;
  FORMIDS: string;
  ERROR_COUNT: number;
}

const GEAR_VALIDATE_CONFIRM: ConfirmationModalConfig = {
  title: 'Confirm',
  message: 'Are you sure you want to validate?',
  iconClass: 'fa fa-sign-out',
  iconColor: 'txt-color-orangeDark',
  confirmButtonText: 'Yes',
  cancelButtonText: 'No',
  confirmButtonClass: 'btn-danger',
};

/**
 * Angular 12 port of the legacy `validation/errors` step
 * (`ui/app/components/efile/efileElements/data/errors.html` +
 * `DataErrorsController` in `EfileElementsController.js` L1550-L1770).
 *
 * Layout mirrors the legacy split panel:
 *
 *  - Left "form list" card: per-form chips with red `ERROR_COUNT` badge.
 *    Loaded via `loadGridJson?grid_id=5779&action_code=9cv61f`. A
 *    "Select All" toggle and refresh icon at the top mirror the legacy
 *    UI; clicking a chip selects only that form (matching legacy
 *    `vm.clickIcon`), the per-row checkbox toggles individual forms.
 *
 *  - Right grid: `loadGridJson?grid_id=5779&action_code=p35kth` with the
 *    selected `form_id` CSV. Reuses {@link EfileGridDirective} for the
 *    full gizmoGrid scaffold (toolbar, filters, batch actions, …).
 *
 *  - "First run" centered card (rendered when the form list returns
 *    zero rows): a big gear that triggers the validate flow on click.
 *    `EFileService.validateSubmission` is the migrated equivalent of
 *    `EfileFactory.validate`.
 *
 *  - Auto-status-update: every successful form-list refresh drives the
 *    same `q05d2n` step-status update used by the other return-review
 *    grids — RESOLVED when the list is empty, FAILED otherwise. We emit
 *    `(stepsReload)` on success so the parent can re-paint the tab strip.
 */
@Component({
    selector: 'gtw-efile-validation-errors',
    templateUrl: './efile-validation-errors.component.html',
    styleUrls: ['./efile-validation-errors.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EfileValidationErrorsComponent
  implements OnInit, OnChanges, OnDestroy {
  /**
   * Legacy `errors.html` hidden spans — load uses `action_code=p35kth` but
   * gizmoGrid export reads `#action_id` / `#screen_key` (32193 / 11212).
   */
  private static readonly LEGACY_EXPORT_ACTION_ID = 32193;
  private static readonly LEGACY_EXPORT_SCREEN_KEY = 11212;

  @Input() step: ReturnReviewStep | null = null;
  @Output() stepsReload = new EventEmitter<void>();

  @ViewChild('gridRef', { static: false }) gridRef?: EfileGridDirective;

  errors: ErrorListRow[] | null = null;
  formCheckbox: boolean[] = [];
  selectAllToggle = true;

  loadingErrors = false;
  loadError = '';
  firstRun = false;
  validating = false;

  /** Becomes truthy once the user has selected at least one form. */
  showErrors = false;
  gridConfig: EfileGridConfig | null = null;

  // Confirmation modal for the validate gear (legacy uses
  // `app/templates/modals/confirm-action.html` — we mirror it inline).
  showConfirmModal = false;
  /** When set, confirming runs this ctrlFn plan and refreshes the errors grid. */
  private pendingCtrlFnPlan: CtrlFnPlan | null = null;

  confirmModalConfig: ConfirmationModalConfig = { ...GEAR_VALIDATE_CONFIRM };

  private subs = new Subscription();
  private lastSubmissionId: string | number | null = null;
  /** Last (submission, status) pair we POSTed q05d2n for, to avoid loops. */
  private lastReportedStatus = '';

  constructor(
    private cdr: ChangeDetectorRef,
    private gridFactory: GridFactory,
    private gridCache: EfileGridCacheService,
    private workflowService: EfileWorkflowService,
    private efileService: EFileService,
    private globalService: GlobalService,
    private appService: AppService,
    private submissionService: SubmissionService,
    private snackbar: GtwSnackbarService,
    private ctrlFnDispatcher: EfileCtrlFnDispatcherService,
  ) {}

  ngOnInit(): void {
    this.refreshIfSubmissionChanged();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['step']) {
      this.refreshIfSubmissionChanged();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // ---------------------------------------------------------------------------
  // Form list (left card)
  // ---------------------------------------------------------------------------

  private refreshIfSubmissionChanged(): void {
    const submissionId = this.submissionService.activeSubmission?.SUBMISSION_ID;
    if (submissionId == null) return;
    if (this.lastSubmissionId === submissionId) return;
    this.lastSubmissionId = submissionId;
    this.lastReportedStatus = '';
    this.formCheckbox = [];
    this.errors = null;
    this.showErrors = false;
    this.gridConfig = null;
    this.pendingCtrlFnPlan = null;
    this.showConfirmModal = false;
    this.loadErrorList();
  }

  /** Public — wired to the refresh icon in the form-list card. */
  refresh(): void {
    this.loadErrorList();
  }

  loadErrorList(): void {
    const submission = this.submissionService.activeSubmission as Submission | undefined;
    if (!submission?.SUBMISSION_ID) return;

    this.loadingErrors = true;
    this.loadError = '';
    this.errors = null;
    this.cdr.markForCheck();

    const url = `${environment.base_url}/loadGridJson?grid_id=5779&action_code=9cv61f`;
    const params = {
      submission_id: submission.SUBMISSION_ID,
      key_id: '',
    };
    const requestKey = `${url}|key_id=&submission_id=${encodeURIComponent(
      String(submission.SUBMISSION_ID),
    )}`;

    this.subs.add(
      this.gridCache
        .loadOnce(requestKey, () => this.gridFactory.loadGridData(url, params))
        .pipe(take(1))
        .subscribe({
          next: (response: any) => {
            this.loadingErrors = false;
            if (String(response?.callSuccess) !== '1') {
              this.loadError =
                'Error occurred fetching data. If this continues please contact support.';
              this.cdr.markForCheck();
              return;
            }
            const rows: ErrorListRow[] = Array.isArray(response.data)
              ? response.data
              : [];

            // Legacy sentinel — first row carries IS_NOT_FIRST_RUN with
            // ERROR_COUNT used as a 0/1 flag (DataErrorsController L1733-L1736).
            if (rows.length > 0 && (rows[0] as any).FORM === 'IS_NOT_FIRST_RUN') {
              const sentinel = rows.shift()!;
              this.firstRun = !sentinel.ERROR_COUNT;
            } else {
              this.firstRun = rows.length === 0;
            }
            this.errors = rows;

            // Re-seed when form count changes: default all selected + grid on +
            // collapsed icon strip (showErrors). Prior per-form state is only kept
            // when the row count matches (same refresh shape).
            if (this.formCheckbox.length !== rows.length) {
              this.selectAllToggle = true;
              this.formCheckbox = rows.map(() => true);
            } else if (this.selectAllToggle) {
              this.formCheckbox = rows.map(() => true);
            }
            this.applyFormSelection();

            // Auto-status-update — RESOLVED when no errors, FAILED otherwise.
            this.updateStepStatus(rows.length === 0 ? 'RESOLVED' : 'FAILED');

            this.cdr.markForCheck();
          },
          error: (err: any) => {
            this.loadingErrors = false;
            this.loadError =
              'Error occurred fetching data. If this continues please contact support.';
            console.error('[EfileValidationErrors] form list load failed', err);
            this.cdr.markForCheck();
          },
        }),
    );
  }

  // ---------------------------------------------------------------------------
  // Form selection → grid filter
  // ---------------------------------------------------------------------------

  onFormCheckboxChange(index: number, _row: ErrorListRow): void {
    this.formCheckbox[index] = !this.formCheckbox[index];
    this.selectAllToggle = this.formCheckbox.every((v) => v);
    this.applyFormSelection();
  }

  onSelectAllToggle(): void {
    this.selectAllToggle = !this.selectAllToggle;
    this.formCheckbox = (this.errors || []).map(() => this.selectAllToggle);
    this.applyFormSelection();
  }

  /** Click handler on each form chip — exclusive selection (legacy `vm.clickIcon`). */
  selectOnly(index: number): void {
    this.formCheckbox = this.formCheckbox.map((_, i) => i === index);
    this.selectAllToggle = this.formCheckbox.every((v) => v);
    this.applyFormSelection();
  }

  private applyFormSelection(): void {
    const submission = this.submissionService.activeSubmission as Submission | undefined;
    const errors = this.errors || [];
    if (!submission?.SUBMISSION_ID || errors.length === 0) {
      this.showErrors = false;
      this.gridConfig = null;
      this.cdr.markForCheck();
      return;
    }

    const formIds = errors
      .filter((_, i) => this.formCheckbox[i])
      .map((row) => row.FORMIDS)
      .filter(Boolean)
      .join(',');

    if (!formIds) {
      this.showErrors = false;
      this.gridConfig = null;
      this.cdr.markForCheck();
      return;
    }

    // Build / update the grid config. Use `cacheKey` so re-selecting the
    // same form-set re-uses the cached gizmoGrid response (mirrors
    // workspaceFactory.getCustomDataCache in legacy DataErrorsController).
    // Export contract (legacy errors.html hidden spans + gizmoGrid exportExcel):
    //   GET  exportToExcel?action_id=32193&grid_id=5779&sso_id=…&screen_key=11212
    //   POST jsonObj={"submission_id":"…","form_id":"…",
    //     "display_mE_code":"true","display_scenario_description":"true",
    //     "display_filing_group_description":"1"}
    //        &headerArray=[…grid column metadata…]
    this.gridConfig = {
      gridId: '5779',
      actionCode: 'p35kth',
      exportActionId: EfileValidationErrorsComponent.LEGACY_EXPORT_ACTION_ID,
      exportScreenKey: EfileValidationErrorsComponent.LEGACY_EXPORT_SCREEN_KEY,
      exportDisplayDefaults: {
        display_mE_code: 'true',
        display_scenario_description: 'true',
        display_filing_group_description: '1',
      },
      extraParams: {
        submission_id: String(submission.SUBMISSION_ID),
        form_id: formIds,
      },
      cacheKey: `EFILE_ERRORS_${submission.SUBMISSION_ID}_${formIds}`,
    };
    this.showErrors = true;
    this.cdr.markForCheck();
  }

  // ---------------------------------------------------------------------------
  // Validate flow (gear button)
  // ---------------------------------------------------------------------------

  onValidateClick(): void {
    const submission = this.submissionService.activeSubmission as Submission | undefined;
    if (!submission?.SUBMISSION_ID) return;

    if ((submission as any).SUBMISSION_STATUS === 'Production Accepted') {
      this.snackbar.openSnackBar(
        'You cannot perform this action on a submission that is already submitted in production.',
        'gtw-snackbar--yellow',
      );
      return;
    }
    this.pendingCtrlFnPlan = null;
    this.confirmModalConfig = { ...GEAR_VALIDATE_CONFIRM };
    this.showConfirmModal = true;
    this.cdr.markForCheck();
  }

  /**
   * gizmoGrid ctrlFns (e.g. toolbar **Validate**) — same dispatcher + confirm
   * flow as {@link EfileReturnReviewComponent.onGridCallback}; that parent never
   * receives events from this nested grid unless we handle them here.
   */
  onErrorsGridCallback(event: EfileGridCallbackEvent): void {
    if (event?.type === 'input') {
      return;
    }
    const plan = this.ctrlFnDispatcher.plan({
      callName: event.callName,
      data: event.data,
      type: event.type,
      workflowStepId: this.step?.WORKFLOW_STEP_ID,
      stateLeaf: 'validation',
    });
    if (!plan) {
      return;
    }

    if (!plan.confirmConfig) {
      const grid = this.gridRef;
      if (!grid) {
        return;
      }
      this.subs.add(
        plan.execute().subscribe((result) => {
          if (result === 'refresh') {
            grid.refresh();
          }
          this.cdr.markForCheck();
        }),
      );
      return;
    }

    this.pendingCtrlFnPlan = plan;
    this.confirmModalConfig = {
      ...this.confirmModalConfig,
      ...plan.confirmConfig,
    };
    this.showConfirmModal = true;
    this.cdr.markForCheck();
  }

  onConfirmModal(): void {
    this.showConfirmModal = false;
    const plan = this.pendingCtrlFnPlan;
    const grid = this.gridRef;
    this.pendingCtrlFnPlan = null;
    this.cdr.markForCheck();

    if (plan && grid) {
      this.subs.add(
        plan.execute().subscribe({
          next: (result) => {
            if (result === 'refresh') {
              grid.refresh();
            }
            this.cdr.markForCheck();
          },
          error: (err: any) => {
            console.error('[EfileValidationErrors] ctrlFn execute failed', err);
            this.cdr.markForCheck();
          },
        }),
      );
      return;
    }

    this.runGearValidateSubmission();
  }

  private runGearValidateSubmission(): void {
    const submission = this.submissionService.activeSubmission as Submission | undefined;
    if (!submission?.SUBMISSION_ID) return;

    this.validating = true;
    this.cdr.markForCheck();

    this.subs.add(
      this.efileService
        .validateSubmission(String(submission.SUBMISSION_ID))
        .pipe(take(1))
        .subscribe({
          next: (response: any) => {
            this.validating = false;
            if (String(response?.callSuccess) === '1') {
              this.loadErrorList();
              this.snackbar.openSnackBar(
                'Validation has started. This process could take several minutes and you will be notified via email once completed.',
                'gtw-snackbar--green',
              );
            } else {
              this.snackbar.openSnackBar(
                response?.errorMessage || 'Something went wrong!',
                'gtw-snackbar--red',
              );
            }
            this.cdr.markForCheck();
          },
          error: (err: any) => {
            this.validating = false;
            console.error('[EfileValidationErrors] validate failed', err);
            this.snackbar.openSnackBar('Something went wrong!', 'gtw-snackbar--red');
            this.cdr.markForCheck();
          },
        }),
    );
  }

  onCancelValidate(): void {
    this.showConfirmModal = false;
    this.pendingCtrlFnPlan = null;
    this.cdr.markForCheck();
  }

  // ---------------------------------------------------------------------------
  // Auto-status-update — q05d2n
  // ---------------------------------------------------------------------------

  private updateStepStatus(status: 'RESOLVED' | 'FAILED'): void {
    const step = this.step;
    const submission = this.submissionService.activeSubmission as Submission | undefined;
    if (!step?.WORKFLOW_STEP_ID || !submission?.SUBMISSION_ID) return;
    if (step.STEP_STATUS === status) return;

    const dedupeKey = `${submission.SUBMISSION_ID}|${step.WORKFLOW_STEP_ID}|${status}`;
    if (this.lastReportedStatus === dedupeKey) return;
    this.lastReportedStatus = dedupeKey;

    const gp = this.globalService.global?.globalParams || {};
    const baseUrl = generalConfigFromGlobal(this.globalService).base_url || '/gtw';
    const ssoId = this.appService.USER_SETTINGS?.user?.sso_id || '';

    this.subs.add(
      this.workflowService
        .updateStepStatus(baseUrl, {
          sso_id: ssoId,
          submission_id: submission.SUBMISSION_ID,
          step_id: step.WORKFLOW_STEP_ID,
          step_status: status,
          tax_year: submission.TAX_YEAR ?? gp.tax_year,
          scenario: gp.scenario,
          jcd_key: 250,
        })
        .subscribe({
          next: (response: any) => {
            if (String(response?.callSuccess) === '1') {
              this.stepsReload.emit();
            }
          },
          error: () => {
            // Allow the next refresh to retry the post.
            this.lastReportedStatus = '';
          },
        }),
    );
  }

  // ---------------------------------------------------------------------------
  // Tracking helpers
  // ---------------------------------------------------------------------------

  trackByIndex = (index: number): number => index;
}
